<script setup lang="ts">
// Admin de la page publique /histoire : contributions du public (pièces jointes privées),
// frise et palmarès. La création et le siège des clubs se corrigent dans Admin > Clubs.
import { computed, onMounted, ref } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';

interface Contribution {
  id: number; created_at: string; name: string | null; email: string; topic: string | null; period: string | null;
  message: string; status: string; admin_note: string | null; files: { index: number; filename: string; type: string }[];
}
interface HistoryEvent { id?: number; date: string; kind: string; region: string; title: string; detail: string | null; source: string | null }
interface Competition { id: number; slug: string; title: string; note: string | null; columns: string[]; rows: (string | null)[][] }

const tab = ref<'contributions' | 'frise' | 'palmares'>('contributions');
const STATUS_LABEL: Record<string, string> = { nouvelle: 'Nouvelle', en_cours: 'En cours', traitee: 'Traitée' };
const KINDS: [string, string][] = [['federation', 'Fédération'], ['ligue', 'Ligue'], ['comite', 'Comité'], ['club', 'Club']];
const REGIONS = ['sud-est', 'aquitaine', 'bretagne', 'pays-de-loire', 'nord', 'est', 'sud-ouest', 'outre-mer'];

// --- Contributions
const contributions = ref<Contribution[]>([]);
const loadContributions = async () => { contributions.value = (await dartsHubApi.get('/history/admin/contributions')).data; };
const newCount = computed(() => contributions.value.filter(c => c.status === 'nouvelle').length);
const updateContribution = async (c: Contribution, patch: Partial<Contribution>) => {
  try {
    const { data } = await dartsHubApi.patch(`/history/admin/contributions/${c.id}`, patch);
    Object.assign(c, data);
    toast.success('Contribution mise à jour.');
  } catch { toast.error('Mise à jour impossible.'); }
};
const deleteContribution = async (c: Contribution) => {
  if (!confirm(`Supprimer la contribution n° ${c.id} et ses pièces jointes ?`)) return;
  await dartsHubApi.delete(`/history/admin/contributions/${c.id}`);
  contributions.value = contributions.value.filter(x => x.id !== c.id);
};
const download = async (c: Contribution, f: { index: number; filename: string }) => {
  const { data } = await dartsHubApi.get(`/history/admin/contributions/${c.id}/files/${f.index}`, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url; a.download = f.filename; a.click();
  URL.revokeObjectURL(url);
};

// --- Frise
const events = ref<HistoryEvent[]>([]);
const editing = ref<HistoryEvent | null>(null);
const loadEvents = async () => { events.value = (await dartsHubApi.get('/history/events')).data; };
const newEvent = () => { editing.value = { date: '', kind: 'comite', region: 'nord', title: '', detail: '', source: 'Journal officiel des associations' }; };
const saveEvent = async () => {
  const e = editing.value!;
  try {
    if (e.id) await dartsHubApi.put(`/history/admin/events/${e.id}`, e);
    else await dartsHubApi.post('/history/admin/events', e);
    editing.value = null;
    await loadEvents();
    toast.success('Frise mise à jour.');
  } catch (err: any) { toast.error(err?.response?.data?.detail?.[0]?.msg || 'Date au format AAAA-MM et titre requis.'); }
};
const deleteEvent = async (e: HistoryEvent) => {
  if (!confirm(`Supprimer « ${e.title} » de la frise ?`)) return;
  await dartsHubApi.delete(`/history/admin/events/${e.id}`);
  await loadEvents();
};

// --- Palmarès
const competitions = ref<Competition[]>([]);
const compIndex = ref(0);
const comp = computed(() => competitions.value[compIndex.value]);
const newSeason = ref('');
const loadPalmares = async () => { competitions.value = (await dartsHubApi.get('/history/palmares')).data; };
const replaceComp = (data: Competition) => { competitions.value[compIndex.value] = data; };
const saveCell = async (season: string, column: string, value: string) => {
  try { replaceComp((await dartsHubApi.put(`/history/admin/palmares/${comp.value.id}/cell`, { season, column, value })).data); }
  catch { toast.error('Enregistrement impossible.'); }
};
const addSeason = async () => {
  if (!/^\d{4}-\d{4}$/.test(newSeason.value)) { toast.error('Saison au format AAAA-AAAA.'); return; }
  await saveCell(newSeason.value, comp.value.columns[0], '?');
  newSeason.value = '';
};
const deleteSeason = async (season: string) => {
  if (!confirm(`Supprimer la saison ${season} de « ${comp.value.title} » ?`)) return;
  replaceComp((await dartsHubApi.delete(`/history/admin/palmares/${comp.value.id}/seasons/${season}`)).data);
};
const saveNote = async () => {
  replaceComp((await dartsHubApi.put(`/history/admin/palmares/${comp.value.id}`, { note: comp.value.note })).data);
  toast.success('Note enregistrée.');
};

onMounted(() => { loadContributions(); loadEvents(); loadPalmares(); });
</script>

<template>
  <div class="page">
    <div class="page-header">
      <h1><i class="fa-solid fa-landmark" /> Histoire</h1>
      <a href="/histoire" target="_blank" class="btn btn-ghost btn-sm"><i class="fa-solid fa-up-right-from-square" /> Voir la page publique</a>
    </div>

    <div class="tabs">
      <button :class="{ active: tab === 'contributions' }" @click="tab = 'contributions'">Contributions <span v-if="newCount" class="badge badge-accent">{{ newCount }}</span></button>
      <button :class="{ active: tab === 'frise' }" @click="tab = 'frise'">Frise</button>
      <button :class="{ active: tab === 'palmares' }" @click="tab = 'palmares'">Palmarès</button>
    </div>

    <!-- Contributions -->
    <div v-if="tab === 'contributions'">
      <p class="hint">Les pièces jointes peuvent contenir des données personnelles (listes de licenciés) : elles ne sont jamais publiées telles quelles. Création et siège des clubs : Admin &gt; Clubs.</p>
      <div v-if="!contributions.length" class="card empty">Aucune contribution pour l'instant.</div>
      <div v-for="c in contributions" :key="c.id" class="card contrib">
        <div class="contrib-head">
          <b>n° {{ c.id }} · {{ c.topic || 'Sans sujet' }}</b>
          <span class="muted">{{ new Date(c.created_at).toLocaleString('fr-FR') }} · {{ c.name || 'anonyme' }} &lt;{{ c.email }}&gt;<template v-if="c.period"> · {{ c.period }}</template></span>
        </div>
        <p class="message">{{ c.message }}</p>
        <div v-if="c.files.length" class="files">
          <button v-for="f in c.files" :key="f.index" class="btn btn-ghost btn-sm" @click="download(c, f)"><i class="fa-solid fa-paperclip" /> {{ f.filename }}</button>
        </div>
        <div class="contrib-actions">
          <select :value="c.status" @change="updateContribution(c, { status: ($event.target as HTMLSelectElement).value })">
            <option v-for="(l, s) in STATUS_LABEL" :key="s" :value="s">{{ l }}</option>
          </select>
          <input :value="c.admin_note || ''" type="text" placeholder="Note interne (suite donnée…)" @change="updateContribution(c, { admin_note: ($event.target as HTMLInputElement).value })" />
          <a :href="`mailto:${c.email}`" class="btn btn-ghost btn-sm"><i class="fa-solid fa-reply" /> Répondre</a>
          <button class="btn btn-danger btn-sm" @click="deleteContribution(c)"><i class="fa-solid fa-trash" /></button>
        </div>
      </div>
    </div>

    <!-- Frise -->
    <div v-if="tab === 'frise'">
      <div class="toolbar"><button class="btn btn-primary btn-sm" @click="newEvent"><i class="fa-solid fa-plus" /> Ajouter un événement</button></div>
      <div v-if="editing" class="card edit">
        <div class="grid">
          <label>Date (AAAA-MM, mois 00 si inconnu)<input v-model="editing.date" type="text" placeholder="1976-05" /></label>
          <label>Type<select v-model="editing.kind"><option v-for="[v, l] in KINDS" :key="v" :value="v">{{ l }}</option></select></label>
          <label>Région<select v-model="editing.region"><option v-for="r in REGIONS" :key="r" :value="r">{{ r }}</option></select></label>
          <label class="wide">Titre<input v-model="editing.title" type="text" /></label>
          <label class="wide">Détail<input v-model="editing.detail" type="text" /></label>
          <label class="wide">Source<input v-model="editing.source" type="text" /></label>
        </div>
        <div class="toolbar"><button class="btn btn-primary btn-sm" @click="saveEvent">Enregistrer</button><button class="btn btn-ghost btn-sm" @click="editing = null">Annuler</button></div>
      </div>
      <div class="card flush">
        <table>
          <thead><tr><th>Date</th><th>Type</th><th>Région</th><th>Titre</th><th>Détail</th><th></th></tr></thead>
          <tbody>
            <tr v-for="e in events" :key="e.id">
              <td>{{ e.date }}</td><td>{{ KINDS.find(k => k[0] === e.kind)?.[1] }}</td><td>{{ e.region }}</td><td>{{ e.title }}</td><td class="muted">{{ e.detail }}</td>
              <td class="actions"><button class="btn btn-ghost btn-sm" @click="editing = { ...e }"><i class="fa-solid fa-pen" /></button>
                <button class="btn btn-danger btn-sm" @click="deleteEvent(e)"><i class="fa-solid fa-trash" /></button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Palmarès -->
    <div v-if="tab === 'palmares' && comp">
      <div class="toolbar">
        <select v-model.number="compIndex"><option v-for="(c, i) in competitions" :key="c.id" :value="i">{{ c.title }}</option></select>
        <input v-model="newSeason" type="text" placeholder="Saison AAAA-AAAA" />
        <button class="btn btn-primary btn-sm" @click="addSeason"><i class="fa-solid fa-plus" /> Ajouter la saison</button>
      </div>
      <div class="card">
        <label class="wide">Note affichée sous l'épreuve<textarea v-model="comp.note" rows="2" /></label>
        <button class="btn btn-ghost btn-sm" @click="saveNote">Enregistrer la note</button>
      </div>
      <p class="hint">Une case vide = non disputé ; « ? » = à compléter. Chaque case s'enregistre en quittant le champ.</p>
      <div class="card flush">
        <table class="palm">
          <thead><tr><th>Saison</th><th v-for="col in comp.columns" :key="col">{{ col }}</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in [...comp.rows].reverse()" :key="r[0]!">
              <td><b>{{ r[0] }}</b></td>
              <td v-for="(v, i) in r.slice(1)" :key="`${r[0]}-${i}`">
                <input :value="v ?? ''" type="text" @change="saveCell(r[0]!, comp.columns[i], ($event.target as HTMLInputElement).value)" />
              </td>
              <td><button class="btn btn-danger btn-sm" @click="deleteSeason(r[0]!)"><i class="fa-solid fa-trash" /></button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tabs { display: flex; gap: .5rem; margin-bottom: 1rem; flex-wrap: wrap; }
.tabs button { border: 1px solid var(--border, #ccd); background: none; padding: .45rem .9rem; border-radius: 999px; cursor: pointer; font: inherit; color: inherit; }
.tabs button.active { background: var(--primary, #1f4e78); color: #fff; border-color: transparent; }
.hint { font-size: .85rem; color: var(--text-muted, #667); margin: .25rem 0 .75rem; }
.muted { color: var(--text-muted, #667); font-size: .85rem; }
.toolbar { display: flex; gap: .5rem; align-items: center; flex-wrap: wrap; margin-bottom: .75rem; }
.contrib { margin-bottom: .75rem; }
.contrib-head { display: flex; flex-direction: column; gap: .15rem; margin-bottom: .5rem; }
.message { white-space: pre-wrap; margin: 0 0 .5rem; }
.files { display: flex; gap: .4rem; flex-wrap: wrap; margin-bottom: .5rem; }
.contrib-actions { display: flex; gap: .5rem; align-items: center; flex-wrap: wrap; }
.contrib-actions input { flex: 1 1 240px; }
.edit .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .6rem; margin-bottom: .6rem; }
.edit label, label.wide { display: grid; gap: .25rem; font-size: .85rem; }
.edit label.wide { grid-column: 1 / -1; }
.flush { padding: 0; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: .85rem; }
th, td { padding: .4rem .6rem; border-bottom: 1px solid var(--border, #dde); text-align: left; vertical-align: top; }
td.actions { white-space: nowrap; }
table.palm input { width: 100%; min-width: 140px; font: inherit; font-size: .85rem; }
</style>
